/**
 * The shared block-parse stage for the research-report Markdown subset (§13.1). The
 * report body is a single Markdown document produced by `renderReportMarkdown`
 * (shared) — a deterministic subset: `#`/`##`/`###` headings, paragraphs, numbered
 * and bulleted lists (inline `[text](url)`, `**bold**`, `` `code` `` are handled at
 * render time). Both the on-screen React renderer (`components/Markdown.tsx`) and the
 * print/PDF HTML emitter (`lib/reportExport.ts`) consume this ONE parser so their
 * output can never silently diverge.
 *
 * `renderReportMarkdown` (shared) is a serializer (structured report → Markdown), and
 * the artifact is persisted only as that Markdown string, so the UI genuinely must
 * re-parse — this is that single re-parse.
 */

export type MarkdownBlock =
  | { type: 'heading'; level: 1 | 2 | 3; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; ordered: boolean; items: string[] };

/** Parse the Markdown into a flat list of blocks (headings, paragraphs, lists). */
export function parseMarkdownBlocks(md: string): MarkdownBlock[] {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: 'paragraph', text: paragraph.join(' ').trim() });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push({ type: 'list', ordered: list.ordered, items: list.items });
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const ordered = /^\s*\d+\.\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);

    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', level: heading[1]!.length as 1 | 2 | 3, text: heading[2]! });
    } else if (ordered) {
      flushParagraph();
      if (!list || !list.ordered) {
        flushList();
        list = { ordered: true, items: [] };
      }
      list.items.push(ordered[1]!);
    } else if (bullet) {
      flushParagraph();
      if (!list || list.ordered) {
        flushList();
        list = { ordered: false, items: [] };
      }
      list.items.push(bullet[1]!);
    } else if (line.trim() === '') {
      flushParagraph();
      flushList();
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();
  return blocks;
}
